import { useRef, useEffect, useCallback, useState } from 'react';

interface PullToRefreshOptions {
    onRefresh: () => Promise<void> | void;
    threshold?: number; // 당겨야 하는 최소 거리 (기본 60px)
    maxPull?: number;   // 최대 당김 거리 (기본 120px)
}

/**
 * Pull-to-Refresh Hook
 * 상단에서 아래로 당기면 새로고침 실행
 * 스크롤이 최상단일 때만 동작
 */
export const usePullToRefresh = ({ onRefresh, threshold = 60, maxPull = 120 }: PullToRefreshOptions) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const startYRef = useRef(0);
    const pullDistRef = useRef(0);
    const isPullingRef = useRef(false);
    const [pullDistance, setPullDistance] = useState(0);
    const [isRefreshing, setIsRefreshing] = useState(false);

    const handleTouchStart = useCallback((e: TouchEvent) => {
        const el = containerRef.current;
        if (!el || isRefreshing) return;
        // 스크롤이 최상단일 때만
        if (el.scrollTop > 0) return;
        startYRef.current = e.touches[0].clientY;
        isPullingRef.current = true;
    }, [isRefreshing]);

    const handleTouchMove = useCallback((e: TouchEvent) => {
        if (!isPullingRef.current || isRefreshing) return;
        const el = containerRef.current;
        if (!el || el.scrollTop > 0) {
            isPullingRef.current = false;
            setPullDistance(0);
            return;
        }

        const currentY = e.touches[0].clientY;
        const diff = currentY - startYRef.current;

        if (diff > 0) {
            // 감속 효과 (당길수록 느려짐)
            const dist = Math.min(diff * 0.5, maxPull);
            pullDistRef.current = dist;
            setPullDistance(dist);
            if (dist > 10) {
                e.preventDefault(); // 브라우저 기본 스크롤 방지
            }
        }
    }, [isRefreshing, maxPull]);

    const handleTouchEnd = useCallback(async () => {
        if (!isPullingRef.current) return;
        isPullingRef.current = false;

        if (pullDistRef.current >= threshold && !isRefreshing) {
            setIsRefreshing(true);
            setPullDistance(threshold); // 새로고침 위치에 고정
            try {
                await onRefresh();
            } catch {}
            setIsRefreshing(false);
        }

        pullDistRef.current = 0;
        setPullDistance(0);
    }, [threshold, isRefreshing, onRefresh]);

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        el.addEventListener('touchstart', handleTouchStart, { passive: true });
        el.addEventListener('touchmove', handleTouchMove, { passive: false });
        el.addEventListener('touchend', handleTouchEnd, { passive: true });

        return () => {
            el.removeEventListener('touchstart', handleTouchStart);
            el.removeEventListener('touchmove', handleTouchMove);
            el.removeEventListener('touchend', handleTouchEnd);
        };
    }, [handleTouchStart, handleTouchMove, handleTouchEnd]);

    return { containerRef, pullDistance, isRefreshing };
};
